/**
 * Best-effort care-time tee for typed encounter / completion forms.
 *
 * Wraps the facade upsert and swallows errors (matches the
 * `logActivityProgress` tee pattern in
 * `packages/core/src/actions/logActivityProgress.action.ts`). Every
 * completion content and typed encounter form's submit handler imports
 * and calls this helper after the encounter / task save has succeeded
 * so that a care-time write failure never rolls back the primary save.
 *
 * @agentic connections
 */

import { useCallback } from "react";
import { useMedplum } from "@medplum/react";
import type { Patient, Task } from "@medplum/fhirtypes";
import {
  getPatientDisplayName,
  getPractitionerDisplayName,
} from "@benchmark/core/resolvers";
import type { CareTimeMethod } from "@benchmark/core/constants";
import { createCareTimeFacade } from "@/facades/care-time.facade";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";

// Input for `useTeeCareTimeForEncounter`. Commented out (knip: unused export)
// alongside the hook below — completion contents use `useTeeCareTimeForTask`.
// export type TeeCareTimeForEncounterInput = {
//   /** Whole minutes captured. Skipped when undefined / ≤ 0. */
//   minutes: number | undefined;
//   /** `Observation.method` to stamp on the tee'd care-time entry. */
//   method: CareTimeMethod;
//   patient: Patient;
//   practitioner: Practitioner;
//   practitionerRole: PractitionerRole;
//   /** Task that triggered the form (becomes Observation.focus). */
//   task?: Task;
//   /** Optional encounter to anchor on `Observation.encounter`. */
//   encounterId?: string;
//   /** Optional notes to store on the care-time Observation. */
//   notes?: string;
// };

const buildCarePlanFromTask = (
  task?: Task,
): { id?: string; display?: string } => {
  const ref = task?.basedOn?.find((b) => b.reference?.startsWith("CarePlan/"));
  if (!ref?.reference?.startsWith("CarePlan/")) return {};
  return {
    id: ref.reference.slice("CarePlan/".length),
    display: ref.display,
  };
};

// Hook factory returning a callback that tees a care-time Observation alongside
// an encounter / completion submit. Commented out (knip: unused export) — no
// current caller uses the encounter-scoped variant; completion contents call
// `useTeeCareTimeForTask` below. Re-enable when a typed-encounter form needs it.
// export const useTeeCareTimeForEncounter = () => {
//   const medplum = useMedplum();
//   return useCallback(
//     async (input: TeeCareTimeForEncounterInput): Promise<void> => {
//       const { minutes, patient, practitioner, practitionerRole } = input;
//       if (!minutes || minutes < 1) return;
//       if (!patient.id || !practitionerRole.id) return;
//       const carePlan = buildCarePlanFromTask(input.task);
//       try {
//         await createCareTimeFacade(medplum).upsert({
//           formData: {
//             minutes,
//             method: input.method,
//             notes: input.notes?.trim() || undefined,
//             effectiveDate: new Date().toISOString(),
//             patientId: patient.id,
//             patientDisplay: getPatientDisplayName(patient),
//             performerRoleId: practitionerRole.id,
//             performerDisplay: getPractitionerDisplayName(practitioner),
//             taskId: input.task?.id,
//             taskDisplay:
//               input.task?.code?.coding?.[0]?.display ?? input.task?.description,
//             carePlanId: carePlan.id,
//             carePlanDisplay: carePlan.display,
//             encounterId: input.encounterId,
//           },
//         });
//       } catch (err) {
//         console.error("Care-time tee failed for encounter/completion:", err);
//       }
//     },
//     [medplum],
//   );
// };

/**
 * Convenience hook for completion / progress contents that have only a
 * `task` in scope. Resolves the practitioner profile + active role via
 * the existing hooks and reads the Patient resource lazily on call.
 *
 * Usage:
 *   const teeForTask = useTeeCareTimeForTask();
 *   await teeForTask({ task, minutes, method: "task-completion" });
 *
 * Mirrors the swallow-errors-by-default semantics of
 * `useTeeCareTimeForEncounter`.
 * @agentic connections
 */
export type TeeCareTimeForTaskInput = {
  task: Task;
  minutes: number | undefined;
  method: CareTimeMethod;
  notes?: string;
  encounterId?: string;
};

/**
 * Tee a care-time entry from a completion / progress flow that only
 * has the `Task` in scope.
 * @agentic connections
 */
export const useTeeCareTimeForTask = () => {
  const medplum = useMedplum();
  const { profile, practitionerRole } = useCurrentPractitionerRole();
  return useCallback(
    async (input: TeeCareTimeForTaskInput): Promise<void> => {
      const { minutes, task, method, notes, encounterId } = input;
      if (!minutes || minutes < 1) return;
      if (!profile || !practitionerRole?.id) return;
      const patientRef = task.for?.reference;
      if (!patientRef?.startsWith("Patient/")) return;
      const patientId = patientRef.slice("Patient/".length);
      let patient: Patient | undefined;
      try {
        patient = (await medplum.readResource("Patient", patientId)) as Patient;
      } catch (err) {
        console.error("Care-time tee: failed to resolve patient:", err);
        return;
      }
      const carePlan = buildCarePlanFromTask(task);
      try {
        await createCareTimeFacade(medplum).upsert({
          formData: {
            minutes,
            method,
            notes: notes?.trim() || undefined,
            effectiveDate: new Date().toISOString(),
            patientId,
            patientDisplay: getPatientDisplayName(patient),
            performerRoleId: practitionerRole.id,
            performerDisplay: getPractitionerDisplayName(profile),
            taskId: task.id,
            taskDisplay: task.code?.coding?.[0]?.display ?? task.description,
            carePlanId: carePlan.id,
            carePlanDisplay: carePlan.display,
            encounterId,
          },
        });
      } catch (err) {
        console.error("Care-time tee failed for task completion:", err);
      }
    },
    [medplum, profile, practitionerRole],
  );
};
